import type { ReactNode } from 'react';
import { PlusSquare, Share } from 'lucide-react';

/**
 * Passo a passo do iPhone para pôr o app na tela de início (o push só funciona
 * por lá). Usado no convite da loja (PRAT-165) e no dos avisos do pedido (PRAT-111).
 * PRAT-201: cada passo tem um desenho do que o cliente vai ver na tela.
 */
export function IosHomeScreenSteps() {
  return (
    <ol className="mt-3 space-y-2 text-[14px] text-gray-800" data-testid="ios-home-screen-steps">
      <Step n={1} art={<SafariBarArt />}>
        Toque em <Share className="inline h-4 w-4 align-[-2px]" aria-label="Compartilhar" />{' '}
        <b>Compartilhar</b>, na barra do Safari
      </Step>
      <Step n={2} art={<ShareSheetArt />}>
        Role e escolha <PlusSquare className="inline h-4 w-4 align-[-2px]" aria-hidden />{' '}
        <b>Adicionar à Tela de Início</b>
      </Step>
      <Step n={3} art={<HomeScreenArt />}>
        Abra o app <b>pelo ícone</b> na tela de início e toque em <b>Ativar avisos</b>
      </Step>
    </ol>
  );
}

function Step({ n, art, children }: { n: number; art: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-gray-50 p-2.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[13px] font-bold text-primary">
        {n}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
      <span className="shrink-0" aria-hidden>
        {art}
      </span>
    </li>
  );
}

/** Barra de baixo do Safari com o botão Compartilhar em destaque. */
function SafariBarArt() {
  return (
    <span className="flex h-12 w-20 flex-col justify-end overflow-hidden rounded-lg border border-gray-200 bg-white">
      <span className="flex items-center justify-around border-t border-gray-200 bg-gray-100 px-1 py-1.5 text-gray-400">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        <span className="relative flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-primary ring-2 ring-primary/40">
          <Share className="h-3 w-3" />
        </span>
        <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      </span>
    </span>
  );
}

/** Folha de compartilhar com "Adicionar à Tela de Início" em destaque. */
function ShareSheetArt() {
  return (
    <span className="flex h-12 w-20 flex-col justify-center gap-1 rounded-lg border border-gray-200 bg-white px-1.5">
      <span className="h-1.5 w-12 rounded bg-gray-200" />
      <span className="flex items-center gap-1 rounded bg-primary/15 px-1 py-0.5 text-primary ring-1 ring-primary/40">
        <PlusSquare className="h-3 w-3 shrink-0" />
        <span className="h-1.5 w-9 rounded bg-primary/50" />
      </span>
      <span className="h-1.5 w-10 rounded bg-gray-200" />
    </span>
  );
}

/** Tela de início com o ícone do app em destaque. */
function HomeScreenArt() {
  return (
    <span className="grid h-12 w-20 grid-cols-4 content-center gap-1 rounded-lg border border-gray-200 bg-gradient-to-b from-sky-100 to-indigo-100 px-1.5">
      {Array.from({ length: 7 }, (_, i) => (
        <span key={i} className="h-3 w-3 rounded-[4px] bg-white/80" />
      ))}
      <span className="h-3 w-3 rounded-[4px] bg-primary ring-2 ring-primary/40" />
    </span>
  );
}
